import React, { useState, useEffect, useRef, useCallback } from "react";
import { CircleAlert, MessageSquareWarning, PenLine, TriangleAlert } from "lucide-react";

/* Branded drop-in replacement for window.prompt / confirm / alert.
   appPrompt(message, defaultValue?, opts?)  -> Promise<string|null>
   appConfirm(message, opts?)                -> Promise<boolean>
   appAlert(message, opts?)                  -> Promise<void>
   opts: { title, confirmText, cancelText, destructive, placeholder } */

let _enqueue = null;
const _pending = [];

function request(cfg) {
  return new Promise((resolve) => {
    const job = { ...cfg, resolve };
    if (_enqueue) _enqueue(job);
    else _pending.push(job);
  });
}

export function appPrompt(message, defaultValue = "", opts = {}) {
  return request({ kind: "prompt", message, defaultValue, ...opts });
}
export function appConfirm(message, opts = {}) {
  return request({ kind: "confirm", message, ...opts });
}
export function appAlert(message, opts = {}) {
  return request({ kind: "alert", message, ...opts });
}

const DESTRUCTIVE_RE = /(delet|remov|discard|revok|disconnect|deactivat|unpublish|permanent|no undo|cannot be undone|cancel your|reset|clear)/i;

const KIND_META = {
  prompt: { kicker: "Input needed", Icon: PenLine },
  confirm: { kicker: "Please confirm", Icon: MessageSquareWarning },
  alert: { kicker: "Heads up", Icon: CircleAlert },
};

export function AppDialogHost() {
  const [queue, setQueue] = useState([]);
  const [value, setValue] = useState("");
  const [closing, setClosing] = useState(false);
  const inputRef = useRef(null);
  const job = queue[0] || null;

  useEffect(() => {
    _enqueue = (j) => setQueue((q) => [...q, j]);
    if (_pending.length) {
      const drained = _pending.splice(0, _pending.length);
      setQueue((q) => [...q, ...drained]);
    }
    return () => { _enqueue = null; };
  }, []);

  useEffect(() => {
    if (job) {
      setValue(job.kind === "prompt" ? String(job.defaultValue ?? "") : "");
      setClosing(false);
      const t = setTimeout(() => {
        if (inputRef.current) { inputRef.current.focus(); inputRef.current.select(); }
      }, 30);
      return () => clearTimeout(t);
    }
  }, [job]);

  const finish = useCallback((result) => {
    if (!job) return;
    setClosing(true);
    setTimeout(() => {
      job.resolve(result);
      setQueue((q) => q.slice(1));
    }, 120);
  }, [job]);

  const onOk = useCallback(() => {
    if (!job) return;
    if (job.kind === "prompt") finish(value);
    else if (job.kind === "confirm") finish(true);
    else finish(undefined);
  }, [job, value, finish]);

  const onCancel = useCallback(() => {
    if (!job) return;
    if (job.kind === "prompt") finish(null);
    else if (job.kind === "confirm") finish(false);
    else finish(undefined);
  }, [job, finish]);

  useEffect(() => {
    if (!job) return;
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onCancel(); }
      else if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); onOk(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [job, onOk, onCancel]);

  if (!job) return null;

  const destructive = job.destructive ?? (job.kind === "confirm" &&
    DESTRUCTIVE_RE.test(`${job.title || ""} ${job.message || ""} ${job.confirmText || ""}`));
  const meta = destructive && job.kind === "confirm"
    ? { kicker: "Please confirm", Icon: TriangleAlert }
    : KIND_META[job.kind];
  const Icon = meta.Icon;
  const confirmText = job.confirmText || (job.kind === "prompt" ? "OK" : job.kind === "confirm" ? (destructive ? "Yes, continue" : "Confirm") : "Got it");
  const cancelText = job.cancelText || "Cancel";

  return (
    <div
      className={`fixed inset-0 z-[200] flex items-center justify-center p-4 transition-opacity duration-150 ${closing ? "opacity-0" : "opacity-100"}`}
      style={{ background: "rgba(3,4,8,0.72)", backdropFilter: "blur(6px)" }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel(); }}
      data-testid="app-dialog-overlay"
    >
      <div
        className={`w-full max-w-md rounded-2xl overflow-hidden transition-all duration-150 ${closing ? "opacity-0 scale-95" : "opacity-100 scale-100"}`}
        style={{
          background: "linear-gradient(160deg, #101017 0%, #0B0B10 55%, #0A0A0E 100%)",
          border: "1px solid rgba(255,255,255,0.09)",
          boxShadow: "0 24px 70px rgba(0,0,0,0.7), 0 0 0 1px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.05) inset",
          animation: closing ? undefined : "appDialogIn 0.18s cubic-bezier(0.16,1,0.3,1)",
        }}
        data-testid="app-dialog"
        role="dialog"
        aria-modal="true"
      >
        <div className="h-[3px] w-full" style={{ background: destructive
          ? "linear-gradient(90deg, #EF4444, #B91C1C 60%, transparent)"
          : "linear-gradient(90deg, #FF8A00, #FFB347 60%, transparent)" }} />
        <div className="p-6">
          <div className="flex items-start gap-4">
            <div
              className="shrink-0 flex items-center justify-center rounded-xl"
              style={{
                width: 42, height: 42,
                background: destructive ? "rgba(239,68,68,0.12)" : "rgba(255,138,0,0.12)",
                border: `1px solid ${destructive ? "rgba(239,68,68,0.35)" : "rgba(255,138,0,0.35)"}`,
              }}
            >
              <Icon size={20} color={destructive ? "#F87171" : "#FF9E2C"} />
            </div>
            <div className="flex-1 min-w-0">
              <div
                className="text-[10px] font-mono uppercase tracking-[0.22em] mb-1"
                style={{ color: destructive ? "#F87171" : "#FF9E2C" }}
              >
                {meta.kicker}
              </div>
              {job.title && (
                <div className="text-base font-semibold text-zinc-100 mb-1">{job.title}</div>
              )}
              <div className="text-sm leading-relaxed whitespace-pre-line" style={{ color: "#A6ACBE" }} data-testid="app-dialog-message">
                {job.message}
              </div>
            </div>
          </div>

          {job.kind === "prompt" && (
            <input
              ref={inputRef}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={job.placeholder || ""}
              className="mt-4 w-full rounded-lg px-3.5 py-2.5 text-sm text-zinc-100 outline-none transition-colors"
              style={{
                background: "#07070B",
                border: "1px solid rgba(255,255,255,0.12)",
              }}
              onFocus={(e) => { e.target.style.border = "1px solid rgba(255,138,0,0.6)"; e.target.style.boxShadow = "0 0 0 3px rgba(255,138,0,0.12)"; }}
              onBlur={(e) => { e.target.style.border = "1px solid rgba(255,255,255,0.12)"; e.target.style.boxShadow = "none"; }}
              data-testid="app-dialog-input"
            />
          )}

          <div className="mt-6 flex items-center justify-end gap-2.5">
            {job.kind !== "alert" && (
              <button
                type="button"
                onClick={onCancel}
                className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                style={{ color: "#9BA1B3", border: "1px solid rgba(255,255,255,0.12)", background: "transparent" }}
                onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(255,255,255,0.05)"; e.currentTarget.style.color = "#E4E6EC"; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "#9BA1B3"; }}
                data-testid="app-dialog-cancel"
              >
                {cancelText}
              </button>
            )}
            <button
              type="button"
              onClick={onOk}
              className="px-5 py-2 rounded-lg text-sm font-semibold transition-all"
              style={destructive
                ? { background: "linear-gradient(180deg, #EF4444, #DC2626)", color: "#fff", boxShadow: "0 4px 14px rgba(239,68,68,0.35)" }
                : { background: "linear-gradient(180deg, #FF9E2C, #FF8A00)", color: "#14100A", boxShadow: "0 4px 14px rgba(255,138,0,0.35)" }}
              onMouseEnter={(e) => { e.currentTarget.style.filter = "brightness(1.08)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.filter = "none"; }}
              data-testid="app-dialog-ok"
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
      <style>{`@keyframes appDialogIn { from { opacity: 0; transform: scale(0.93) translateY(8px); } to { opacity: 1; transform: scale(1) translateY(0); } }`}</style>
    </div>
  );
}
